import React, { createContext, useState, useEffect, useCallback } from 'react';
import { discountsApi } from '../api/discounts';
import { publicApi } from '../api/public';
import { departmentsApi } from '../api/departments';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [recentDiscounts, setRecentDiscounts] = useState([]);
  const [expiringDiscounts, setExpiringDiscounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showMarquee, setShowMarquee] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Функція для завантаження нещодавніх знижок
  const fetchRecentDiscounts = useCallback(async () => {
    try {
      setError(null);
      
      const response = await discountsApi.getRecentDiscounts(5);
      
      if (response.data && Array.isArray(response.data)) {
        setRecentDiscounts(response.data);
      } else {
        console.warn('Неочікувана структура даних для нещодавніх знижок:', response.data);
        setRecentDiscounts([]);
      }
    } catch (error) {
      console.error('Помилка при завантаженні нещодавніх знижок:', error);
      setError('Помилка при завантаженні нещодавніх знижок');
      setRecentDiscounts([]);
    }
  }, []);

  // Функція для завантаження знижок, що закінчуються
  const fetchExpiringDiscounts = useCallback(async () => {
    try {
      
      const response = await discountsApi.getExpiringDiscounts();
      
      if (response.data && Array.isArray(response.data)) {
        setExpiringDiscounts(response.data);
      } else {
        console.warn('Неочікувана структура даних для знижок, що закінчуються:', response.data);
        setExpiringDiscounts([]);
      }
    } catch (error) {
      console.error('Помилка при завантаженні знижок, що закінчуються:', error);
      setExpiringDiscounts([]);
    }
  }, []);

  // Функція для завантаження категорій
  const fetchCategories = useCallback(async () => {
    try {
      
      const response = await publicApi.getCategories();
      
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        console.warn('Неочікувана структура даних для категорій:', response.data);
        setCategories([]);
      }
    } catch (error) {
      console.error('Помилка при завантаженні категорій:', error);
      setCategories([]);
    }
  }, []);

  // Функція для завантаження департаментів
  const fetchDepartments = useCallback(async () => {
    try {
      
      try {
        // Спочатку пробуємо публічний ендпоінт
        const publicResponse = await publicApi.getDepartments();
        
        if (publicResponse.data && Array.isArray(publicResponse.data)) {
          setDepartments(publicResponse.data);
          return;
        }
      } catch (publicError) {
        console.warn('AppContext - Публічний API департаментів недоступний, спробуємо основний API');
      }
      
      try {
        // Якщо публічний не працює, пробуємо основний API
        const mainResponse = await departmentsApi.getAllDepartments();
        
        if (mainResponse.data && Array.isArray(mainResponse.data)) {
          setDepartments(mainResponse.data);
          return;
        }
      } catch (mainError) {
        console.warn('AppContext - Основний API департаментів також недоступний');
      }
      
      // Якщо обидва API не працюють, встановлюємо порожній масив
      console.warn('AppContext - Не вдалося завантажити департаменти з жодного API');
      setDepartments([]);
      
    } catch (error) {
      console.error('AppContext - Загальна помилка при завантаженні департаментів:', error);
      setDepartments([]);
    }
  }, []);

  // Функція для оновлення всіх даних
  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRecentDiscounts(),
        fetchExpiringDiscounts(),
        fetchCategories(),
        fetchDepartments()
      ]);
    } catch (error) {
      console.error('Помилка при оновленні даних:', error);
      setError('Помилка при завантаженні даних');
    } finally {
      setLoading(false);
    }
  }, [fetchRecentDiscounts, fetchExpiringDiscounts, fetchCategories, fetchDepartments]);

  // Початкове завантаження даних
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Періодичне оновлення даних кожні 5 хвилин
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRecentDiscounts();
      fetchExpiringDiscounts();
    }, 5 * 60 * 1000); // 5 хвилин

    return () => clearInterval(interval);
  }, [fetchRecentDiscounts, fetchExpiringDiscounts]);

  const contextValue = {
    // Дані
    recentDiscounts,
    expiringDiscounts,
    categories,
    departments,
    
    // Стан
    loading,
    error,
    showMarquee,
    
    // Функції для управління станом
    setShowMarquee,
    setError,
    
    // Функції для оновлення даних
    fetchRecentDiscounts,
    fetchExpiringDiscounts,
    fetchCategories,
    fetchDepartments,
    refreshData,
    
    // Функції для очищення даних
    clearRecentDiscounts: () => setRecentDiscounts([]),
    clearExpiringDiscounts: () => setExpiringDiscounts([]),
    clearCategories: () => setCategories([]),
    clearDepartments: () => setDepartments([]),
    
    // Функції для додавання/оновлення окремих елементів
    addRecentDiscount: (discount) => {
      setRecentDiscounts(prev => [discount, ...prev.slice(0, 4)]);
    },
    
    updateDiscount: (updatedDiscount) => {
      setRecentDiscounts(prev => 
        prev.map(d => d.id === updatedDiscount.id ? updatedDiscount : d)
      );
      setExpiringDiscounts(prev => 
        prev.map(d => d.id === updatedDiscount.id ? updatedDiscount : d)
      );
    },
    
    removeDiscount: (discountId) => {
      setRecentDiscounts(prev => prev.filter(d => d.id !== discountId));
      setExpiringDiscounts(prev => prev.filter(d => d.id !== discountId));
    }
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;